import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Loader2, Save, ShieldCheck } from 'lucide-react';
import { adminApi } from '../services/api';
import MesIdentifiants from '../components/forms/MesIdentifiants';
import MaSignature from '../components/MaSignature';
import { annoncerSucces, casDeLErreur, Squelette } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════════
   MON PROFIL

   Cette page existe parce que « Paramètres » abritait le profil. Les deux mots
   ne désignent pas la même chose, et les confondre a un coût concret : on
   cherche où changer son mot de passe dans une page dont le titre annonce des
   réglages, et on ne trouve aucun réglage dans la seule page qui en promet.

   La ligne de partage tient en une phrase :
     - ici, ce QUI je suis — mon identité, mon accès, ma clé ;
     - dans « Paramètres », ce que je RÈGLE — l'apparence, mes notifications.

   Trois blocs, dans l'ordre où on les consulte : qui je suis (la fiche), ce
   que je peux changer (le formulaire), et comment je me connecte (le mot de
   passe). La fiche est en lecture seule : le rôle et la date d'entrée ne se
   modifient pas soi-même, et les présenter dans un champ de saisie grisé
   laisserait croire le contraire.
   ═══════════════════════════════════════════════════════════════════════════ */

const CHAMPS_MOT_DE_PASSE = [
  { cle: 'ancien', libelle: 'Mot de passe actuel', auto: 'current-password' },
  { cle: 'nouveau', libelle: 'Nouveau mot de passe', auto: 'new-password' },
  { cle: 'confirmation', libelle: 'Confirmer le nouveau', auto: 'new-password' },
];

/* Le rôle est un code technique dans la base (`SUPER_ADMIN`). L'écrire tel
   quel sur la fiche de quelqu'un donne à sa propre page un air de table SQL. */
const ROLES_LISIBLES = {
  SUPER_ADMIN: 'Super administrateur',
  ADMIN: 'Administrateur',
  ANALYSTE: 'Analyste',
  COACH: 'Coach / Expert',
  DIRIGEANT: "Dirigeant d'association",
  MEMBRE: "Membre d'association",
};

function Fait({ libelle, valeur, enChargement = false }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
        {libelle}
      </p>
      {/* Pendant le chargement, un « — » se lirait comme « non renseigné ». */}
      {enChargement && !valeur ? (
        <Squelette className="h-4 w-24 mt-1.5" />
      ) : (
        <p className="text-[14px] font-semibold text-[var(--text-bold)] mt-0.5 break-words">
          {valeur || '—'}
        </p>
      )}
    </div>
  );
}

export default function Profile({ user, onProfileUpdated }) {
  const [profil, setProfil] = useState(user || null);
  const [fiche, setFiche] = useState(null);

  // Le changement de mot de passe garde son état à lui : un échec sur le
  // formulaire d'identité ne doit pas faire perdre une saisie en cours ici.
  const [mdp, setMdp] = useState({ ancien: '', nouveau: '', confirmation: '' });
  const [mdpEnCours, setMdpEnCours] = useState(false);
  const [mdpRetour, setMdpRetour] = useState({ type: '', message: '' });

  useEffect(() => { setProfil(user || null); }, [user]);

  // 'chargement' | 'pret' | 'erreur' — la fiche se recharge depuis l'avis d'échec.
  const [etatFiche, setEtatFiche] = useState('chargement');
  const [erreurFiche, setErreurFiche] = useState(null);
  const [essai, setEssai] = useState(0);
  const recharger = useCallback(() => setEssai((n) => n + 1), []);

  useEffect(() => {
    let actif = true;
    setEtatFiche('chargement');
    adminApi.getMesIdentifiants()
      .then((d) => { if (actif) { setFiche(d); setEtatFiche('pret'); } })
      .catch((e) => { if (actif) { setErreurFiche(e); setEtatFiche('erreur'); } });
    return () => { actif = false; };
  }, [essai]);
  const ficheEnChargement = etatFiche === 'chargement' && !fiche;

  const avatarLabel = (profil?.username || '?').charAt(0).toUpperCase();
  const role = fiche?.role || profil?.role || '';
  const nomComplet = [fiche?.first_name, fiche?.last_name].filter(Boolean).join(' ');

  const changerMotDePasse = async () => {
    setMdpRetour({ type: '', message: '' });
    if (!mdp.ancien || !mdp.nouveau) {
      setMdpRetour({ type: 'error', message: 'Renseignez le mot de passe actuel et le nouveau.' });
      return;
    }
    if (mdp.nouveau !== mdp.confirmation) {
      setMdpRetour({ type: 'error', message: 'La confirmation ne correspond pas au nouveau mot de passe.' });
      return;
    }
    setMdpEnCours(true);
    try {
      const r = await adminApi.changerMonMotDePasse(mdp.ancien, mdp.nouveau);
      // L'ancien mot de passe rend caducs les jetons en cours : on garde
      // la session ouverte avec ceux que le serveur vient d'émettre.
      if (r?.access) localStorage.setItem('asso_acc_token', r.access);
      if (r?.refresh) localStorage.setItem('asso_acc_refresh', r.refresh);
      setMdp({ ancien: '', nouveau: '', confirmation: '' });
      annoncerSucces(r?.message || 'Mot de passe modifié.');
    } catch (e) {
      setMdpRetour({ type: 'error', message: e.message });
    } finally {
      setMdpEnCours(false);
    }
  };

  return (
    <div className="space-y-6 animate-in">
      <div>
        <h1 className="titre-page">Mon profil</h1>
        <p className="text-[13px] text-[var(--text-muted)] mt-1">
          Votre identité, votre accès et votre mot de passe. Les réglages de
          l'interface sont dans <span className="font-semibold">Paramètres</span>.
        </p>
      </div>

      {/* ── La fiche ───────────────────────────────────────────────────────
          En lecture seule, et dite comme telle. */}
      <section className="rounded-carte shadow-carte bg-[var(--bg-card)] p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-16 h-16 shrink-0 rounded-carte flex items-center justify-center
                          text-2xl font-semibold text-[#0A1128]"
               style={{ background: 'var(--accent)' }}
               aria-hidden="true">
            {avatarLabel}
          </div>
          <div className="min-w-0">
            <p className="text-[18px] font-semibold text-[var(--text-bold)] break-words">
              {nomComplet || profil?.username || '—'}
            </p>
            {ficheEnChargement ? (
              <Squelette className="h-3 w-48 mt-1.5" />
            ) : (
              <p className="text-[13px] text-[var(--text-muted)] break-words">
                {fiche?.email || '—'}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-controle text-[11px] font-semibold
                               bg-[var(--accent)]/12 text-[var(--accent-texte)] border border-[var(--accent)]/25">
                <ShieldCheck className="w-3 h-3" aria-hidden="true" />
                {ROLES_LISIBLES[role] || role || '—'}
              </span>
              {fiche?.profil_display && (
                <span className="inline-block px-2.5 py-1 rounded-controle text-[11px] font-semibold
                                 border border-[var(--border)] text-[var(--text-muted)]">
                  {fiche.profil_display}
                </span>
              )}
              {fiche && fiche.is_active === false && (
                <span className="inline-block px-2.5 py-1 rounded-controle text-[11px] font-semibold
                                 bg-[var(--alerte)]/10 text-[var(--alerte)]">
                  Compte suspendu
                </span>
              )}
            </div>
          </div>
        </div>

        {etatFiche === 'erreur' && !fiche && (
          <div role="alert" className="encart encart-attention mt-5 flex items-center justify-between gap-3 flex-wrap">
            <span>
              {casDeLErreur(erreurFiche) === 'horsLigne'
                ? 'Vous êtes hors ligne : votre fiche n’a pas pu être chargée.'
                : 'Votre fiche n’a pas pu être chargée.'}
            </span>
            <button type="button" className="act act-sm act-contour" onClick={recharger}>Réessayer</button>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-5 border-t border-[var(--border)]">
          <Fait libelle="Identifiant" valeur={fiche?.username || profil?.username} enChargement={ficheEnChargement} />
          <Fait libelle="Référence" valeur={fiche?.id} enChargement={ficheEnChargement} />
          <Fait
            enChargement={ficheEnChargement}
            libelle="Compte créé le"
            valeur={fiche?.date_joined
              ? new Date(fiche.date_joined).toLocaleDateString('fr-FR',
                  { day: '2-digit', month: 'long', year: 'numeric' })
              : ''}
          />
          <Fait
            enChargement={ficheEnChargement}
            libelle="Première connexion"
            valeur={fiche ? (fiche.first_login ? 'Pas encore faite' : 'Effectuée') : ''}
          />
        </div>
      </section>

      <MesIdentifiants
        onModifie={(lot) => {
          setProfil((p) => ({ ...(p || {}), ...lot }));
          setFiche((f) => ({ ...(f || {}), ...lot }));
          onProfileUpdated?.(lot);
        }}
      />

      {/* ── Mot de passe ───────────────────────────────────────────────────
          Le mot de passe actuel est exigé : sans lui, une session laissée
          ouverte suffirait à verrouiller le compte de son titulaire. */}
      <section id="mot-de-passe"
               className="rounded-carte shadow-carte bg-[var(--bg-card)] p-6">
        <h2 className="titre-carte text-[var(--text-bold)]">Mot de passe</h2>
        <p className="text-[12.5px] text-[var(--text-muted)] mt-1 mb-5 max-w-xl leading-relaxed">
          Changez votre mot de passe vous-même. Personne d'autre n'en prend connaissance.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CHAMPS_MOT_DE_PASSE.map((champ) => (
            <div key={champ.cle}>
              <label htmlFor={`mdp-${champ.cle}`}
                     className="block text-[13px] font-medium text-[var(--text-main)] mb-1.5">
                {champ.libelle}
              </label>
              <input
                id={`mdp-${champ.cle}`}
                type="password"
                autoComplete={champ.auto}
                value={mdp[champ.cle]}
                onChange={(e) => setMdp((m) => ({ ...m, [champ.cle]: e.target.value }))}
                style={{ minHeight: 44 }}
                className="champ"
              />
            </div>
          ))}
        </div>

        {/* L'erreur reste sous les champs ; la réussite passe en toast. */}
        {mdpRetour.message ? (
          <div role="alert"
               className="mt-4 flex items-start gap-2 text-[13px] text-[var(--alerte)]">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span className="leading-relaxed">{mdpRetour.message}</span>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={changerMotDePasse}
            disabled={mdpEnCours}
            aria-busy={mdpEnCours || undefined}
            style={{ minHeight: 44, backgroundColor: 'var(--accent)', color: '#0A1128' }}
            className="act act-tertiaire"
          >
            {mdpEnCours
              ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              : <Save className="w-4 h-4" aria-hidden="true" />}
            Modifier le mot de passe
          </button>
        </div>
      </section>

      {/* ── La signature ───────────────────────────────────────────────────
          Elle vit ici, avec l'identité et le mot de passe, et non dans
          l'écran des documents : elle appartient à la personne, pas au
          document. On l'enregistre une fois, on signe ensuite partout. */}
      <MaSignature />
    </div>
  );
}
